import { BRAND_WORDMARK } from '../brand';
import { BRANDING, brandingLocalita } from '../branding';
import { BRAND_PRIMARY, BRAND_PRIMARY_DARK } from '../brandColors';
import type { RenderContext } from '@email/types.ts';

/**
 * Il contesto per l'anteprima nel browser.
 *
 * Il renderer vero sta in supabase/functions/_shared/email/: e' lo stesso file
 * che gira dentro le edge function al momento dell'invio. Qui si costruisce
 * solo cio' che il renderer non puo' sapere da solo, leggendolo dal branding
 * dell'app invece che dalle variabili d'ambiente del server.
 *
 * Che i due mondi producano lo stesso HTML lo verifica
 * `npm run verify:email-render`, che li fa girare entrambi e confronta il
 * risultato byte per byte.
 */

const PUBLIC_APP_URL = (import.meta.env?.VITE_PUBLIC_APP_URL as string | undefined)?.replace(/\/+$/, '') || '';

/**
 * Indirizzo assoluto di un file pubblico.
 * In sviluppo l'origine e' localhost, e un'immagine localhost dentro
 * un'anteprima di email e' un riquadro rotto: si usa il dominio pubblico.
 */
function urlAssoluto(percorso: string): string {
  if (typeof window !== 'undefined') {
    const origine = window.location.origin;
    if (origine.includes('localhost') || origine.includes('127.0.0.1')) {
      return PUBLIC_APP_URL ? `${PUBLIC_APP_URL}${percorso}` : `${origine}${percorso}`;
    }
    return `${origine}${percorso}`;
  }
  return PUBLIC_APP_URL ? `${PUBLIC_APP_URL}${percorso}` : percorso;
}

/** Contesto per l'anteprima: link di disiscrizione disattivati, niente tracciamento. */
export function contestoAnteprima(): RenderContext {
  return {
    baseUrl: PUBLIC_APP_URL || (typeof window !== 'undefined' ? window.location.origin : ''),
    logoUrl: urlAssoluto(BRAND_WORDMARK),
    branding: {
      ragioneSociale: BRANDING.ragioneSociale,
      nomeBreve: BRANDING.nomeBreve,
      indirizzo: BRANDING.indirizzo,
      localita: brandingLocalita(),
      partitaIva: BRANDING.partitaIva,
      email: BRANDING.email,
      sito: BRANDING.sito,
    },
    colori: { primario: BRAND_PRIMARY, primarioScuro: BRAND_PRIMARY_DARK },
    unsubscribeUrl: '',
    preferenzeUrl: '',
    anteprima: true,
  };
}

/**
 * Contesto per una email scritta a un lead.
 *
 * Non e' un'anteprima -- questa parte davvero -- ma non nasce da un'iscrizione:
 * il lead ci ha scritto lui. Non c'e' un consenso da revocare ne' preferenze da
 * gestire, quindi il piede lo dice a parole invece di offrire un link che non
 * porterebbe da nessuna parte. I dati aziendali restano, perche' quelli vanno
 * su ogni comunicazione commerciale, anche su una risposta singola.
 */
export function contestoLead(): RenderContext {
  return {
    ...contestoAnteprima(),
    anteprima: false,
    senzaIscrizione: true,
  };
}

/** I dati del lead nei panni di un destinatario, cosi' i segnaposto del
 *  modello ({{nome}}, {{azienda}}, ...) si riempiono come in una campagna. */
export function destinatarioDaLead(lead: {
  email?: string | null;
  full_name?: string | null;
  company_name?: string | null;
  city?: string | null;
  province?: string | null;
}) {
  return {
    email: lead.email ?? '',
    nome: lead.full_name?.trim().split(/\s+/)[0] ?? null,
    nomeCompleto: lead.full_name ?? null,
    azienda: lead.company_name ?? null,
    citta: lead.city ?? null,
    provincia: lead.province ?? null,
    campi: null,
  };
}

/** Destinatario finto per l'anteprima, cosi' i segnaposto si vedono risolti. */
export const DESTINATARIO_ESEMPIO = {
  email: 'mario.rossi@esempio.it',
  nome: null,
  nomeCompleto: 'Mario Rossi',
  azienda: 'Rossi S.r.l.',
  citta: 'Pescara',
  provincia: 'PE',
  campi: null,
};
